import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { apiFetch, mediaUrl } from '../apiBase';
import CountUp from '../components/CountUp';

const PLATFORM_LABEL = { raingg: 'Rain.gg', betdice: 'BetDice', both: 'Rain.gg + BetDice' };

export default function Store() {
  const { user, login, refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [confirmItem, setConfirmItem] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  async function loadItems() {
    const res = await apiFetch('/api/store/items');
    setItems(await res.json());
  }

  useEffect(() => {
    loadItems();
  }, []);

  async function handleRedeem() {
    if (!confirmItem) return;
    setBusy(true);
    setError('');
    try {
      const res = await apiFetch('/api/store/redeem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemId: confirmItem.id }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Redeem failed');
      setToast(`Redeemed ${confirmItem.name}! Staff will fulfil it soon.`);
      setConfirmItem(null);
      await Promise.all([loadItems(), refresh()]);
      setTimeout(() => setToast(''), 4000);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-16">
      <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
        <h1 className="font-display-lg uppercase text-3xl">Store</h1>
        {user && (
          <div className="rounded-xl px-4 py-2 bg-[#12121a] border border-[#F6BB4248] flex items-center gap-2">
            <span className="font-mono-brand text-[10px] text-[#7d7d8c] uppercase tracking-widest">Balance</span>
            <span className="font-mono-brand text-lg font-black text-[#F6BB42]"><CountUp value={user.points} /></span>
          </div>
        )}
      </div>

      {toast && (
        <div className="mb-6 rounded-lg px-4 py-3 bg-[#00E701]/10 border border-[#00E701]/40 text-[#00E701] text-sm font-bold">
          {toast}
        </div>
      )}

      {!items.length ? (
        <div className="text-center text-[#888899] py-20">No items in the store yet — check back soon.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {items.map((item, i) => (
            <div
              key={item.id}
              className="scroll-reveal bg-[#0e0e15] border border-[#23232e] rounded-xl overflow-hidden flex flex-col"
              style={{ transitionDelay: `${Math.min(i, 9) * 70}ms` }}
            >
              <div className="h-28 bg-gradient-to-b from-[#1b1b25] to-[#0c0c12] flex items-center justify-center border-b border-[#23232e]">
                {item.image_url ? (
                  <img src={mediaUrl(item.image_url)} alt={item.name} className="max-h-20 object-contain" />
                ) : (
                  <span className="material-symbols-outlined text-3xl text-[#F6BB42]">redeem</span>
                )}
              </div>
              <div className="p-4 flex flex-col gap-2 flex-1">
                <div className="font-mono-brand text-[9px] uppercase tracking-widest text-[#7d7d8c]">
                  {item.category}
                </div>
                <div className="flex-1 flex flex-col gap-1.5">
                  <div className="text-sm font-bold leading-snug">{item.name}</div>
                  {item.description && (
                    <p className="text-xs leading-relaxed text-[#888899] whitespace-pre-line break-words">{item.description}</p>
                  )}
                </div>
                {item.required_platform && (
                  <div className="font-mono-brand text-[9px] uppercase tracking-widest text-[#F6BB42] flex items-center gap-1">
                    🔒 {PLATFORM_LABEL[item.required_platform] || item.required_platform} only
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="font-mono-brand text-sm font-black text-[#F6BB42]">
                    🪙 <CountUp value={item.price} />
                  </span>
                  <span className="font-mono-brand text-[10px] text-[#7d7d8c]">{item.stock} left</span>
                </div>
                <button
                  disabled={item.stock <= 0}
                  onClick={() => (user ? setConfirmItem(item) : login())}
                  className="btn-primary shine-hover rounded-lg py-2 text-xs mt-1 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {item.stock <= 0 ? 'Out of stock' : user ? 'Redeem' : 'Sign in to redeem'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {confirmItem && createPortal(
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,.82)', backdropFilter: 'blur(3px)' }}>
          <div className="w-full max-w-md rounded-2xl border border-[#24242e] p-6" style={{ background: 'linear-gradient(180deg,#14141c,#0c0c12)' }}>
            <h3 className="font-display-lg text-lg mb-2">Confirm redeem: {confirmItem.name}</h3>
            <p className="text-sm text-[#888899] mb-4">
              Items left: {confirmItem.stock} · Price: 🪙 {confirmItem.price.toLocaleString()}
            </p>
            {error && <p className="text-sm text-[#ff544a] mb-4">{error}</p>}
            <div className="flex gap-3 justify-end pt-4 border-t border-[#1e1e28]">
              <button
                onClick={() => setConfirmItem(null)}
                className="px-4 py-2 rounded-lg text-xs font-black uppercase"
                style={{ background: '#b8352c', color: '#fff' }}
              >
                Cancel
              </button>
              <button
                onClick={handleRedeem}
                disabled={busy}
                className="px-4 py-2 rounded-lg text-xs font-black uppercase"
                style={{ background: '#4caf50', color: '#fff', boxShadow: '0 6px 20px rgba(76,175,80,.28)' }}
              >
                {busy ? 'Redeeming…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
